import { useEffect } from "react";
import type { RefObject } from "react";
import { flushPendingScrollRequest } from "./scroll-controller-pending-flush";
import type {
  ScrollBottomOptions,
  ScrollControllerElementRefs,
  ScrollTargetOptions,
} from "./scroll-controller-types";

interface UseScrollContainerObserverInput extends ScrollControllerElementRefs {
  scrollRef: RefObject<HTMLDivElement | null>;
  updateNearBottom: () => void;
  scrollToBottom: (opts?: ScrollBottomOptions) => void;
  scrollToMessageId: (messageId: string, opts?: ScrollTargetOptions) => void;
  scrollToVersionId: (versionShortId: string, opts?: ScrollTargetOptions) => void;
}

export function useScrollContainerObserver({
  scrollRef,
  observerRef,
  visibleMessageIdsRef,
  messageElementsRef,
  versionElementsRef,
  elementToMessageIdRef,
  pendingScrollRef,
  updateNearBottom,
  scrollToBottom,
  scrollToMessageId,
  scrollToVersionId,
}: UseScrollContainerObserverInput): void {
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    startScrollContainerObserver({
      el,
      observerRef,
      visibleMessageIdsRef,
      messageElementsRef,
      elementToMessageIdRef,
    });
    updateNearBottom();
    flushPendingScrollRequest({
      pendingScrollRef,
      messageElementsRef,
      versionElementsRef,
      scrollToBottom,
      scrollToMessageId,
      scrollToVersionId,
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [
    scrollRef,
    observerRef,
    visibleMessageIdsRef,
    messageElementsRef,
    versionElementsRef,
    elementToMessageIdRef,
    pendingScrollRef,
    updateNearBottom,
    scrollToBottom,
    scrollToMessageId,
    scrollToVersionId,
  ]);
}

function startScrollContainerObserver({
  el,
  observerRef,
  visibleMessageIdsRef,
  messageElementsRef,
  elementToMessageIdRef,
}: Pick<
  UseScrollContainerObserverInput,
  | "observerRef"
  | "visibleMessageIdsRef"
  | "messageElementsRef"
  | "elementToMessageIdRef"
> & {
  el: HTMLDivElement;
}): void {
  observerRef.current?.disconnect();
  visibleMessageIdsRef.current = new Set();
  observerRef.current = createMessageIntersectionObserver({
    el,
    elementToMessageIdRef,
    visibleMessageIdsRef,
  });
  observeRegisteredMessages({
    observerRef,
    messageElementsRef,
    elementToMessageIdRef,
  });
}

function createMessageIntersectionObserver({
  el,
  elementToMessageIdRef,
  visibleMessageIdsRef,
}: Pick<
  UseScrollContainerObserverInput,
  "elementToMessageIdRef" | "visibleMessageIdsRef"
> & {
  el: HTMLDivElement;
}): IntersectionObserver {
  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        updateVisibleMessageIds({
          entry,
          elementToMessageIdRef,
          visibleMessageIdsRef,
        });
      });
    },
    { root: el, threshold: [0, 0.1, 0.5, 1] },
  );
}

function updateVisibleMessageIds({
  entry,
  elementToMessageIdRef,
  visibleMessageIdsRef,
}: Pick<
  UseScrollContainerObserverInput,
  "elementToMessageIdRef" | "visibleMessageIdsRef"
> & {
  entry: IntersectionObserverEntry;
}): void {
  const target = entry.target as HTMLElement;
  const id = elementToMessageIdRef.current.get(target) ?? null;
  if (!id) return;

  if (entry.isIntersecting) {
    visibleMessageIdsRef.current.add(id);
  } else {
    visibleMessageIdsRef.current.delete(id);
  }
}

function observeRegisteredMessages({
  observerRef,
  messageElementsRef,
  elementToMessageIdRef,
}: Pick<
  UseScrollContainerObserverInput,
  "observerRef" | "messageElementsRef" | "elementToMessageIdRef"
>): void {
  messageElementsRef.current.forEach((msgEl) => {
    const msgId = elementToMessageIdRef.current.get(msgEl) ?? null;
    if (!msgId) return;
    observerRef.current?.observe(msgEl);
  });
}
